import { useEffect, useRef } from 'react';
import { playerRepository } from '../../data/playerRepository';
import type { FonteReproducao, PerfilAudio } from '@/shared/types';

export interface PlayerRemoteCommandsDeps {
  onBroadcastEstadoPlayer(): void;
  onHdmiRefresh(): Promise<unknown>;
  onHdmiSelect(deviceId: string): void;
  onTogglePlay(): void;
  /** true quando origem !== transmissao — next_media avança a fila/deck. */
  getPodeAvancarMidia(): boolean;
  onNextMedia(): void;
  getPlaylistLength(): number;
  onPrevMedia(len: number): void;
  onToggleMute(): void;
  onPlayItem(index: number): void;
  onDeleteFilaItem(id: string): void;
  telaId: string | undefined;
  onToggleQrcode(): void;
  onToggleRandom(): void;
  onDisplayFlashMessage(msg: { text: string; style: 'modal' | 'ticker'; duration: number }): void;
  onClearFlashMessage(): void;
  onSetGanhoCaptura(ganho: number): void;
  onSetPerfilAudio(perfil: PerfilAudio): void;
  onSetOrigemMidia(origem: FonteReproducao, audio: 'playlist' | 'captura'): void;
  onToggleNetworkDebug(): void;
  onReloadPlaylist(): void;
  onHypeMoment(force: boolean): void;
  onPdvTesteDestaque(): void;
  onSetVolume(volume: number): void;
}

/**
 * Dispatcher de `remote_cmd` do Controle Remoto.
 * usePlayerSockets já isola via cbRef — não precisa de useCallback aqui.
 * deps via cbRef para quebrar a dependência circular com remoteState.
 */
export function usePlayerRemoteCommands(deps: PlayerRemoteCommandsDeps) {
  const depsRef = useRef(deps);
  useEffect(() => { depsRef.current = deps; });

  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const handleRemoteCmd = (cmd: string, payload: Record<string, unknown>) => {
    const d = depsRef.current;
    switch (cmd) {
      case 'solicitar_estado':
        d.onBroadcastEstadoPlayer();
        void d.onHdmiRefresh();
        break;
      case 'solicitar_hdmi_estado':
        void d.onHdmiRefresh();
        break;
      case 'set_hdmi_device': {
        const { device_id } = payload as { device_id: string };
        if (typeof device_id === 'string' && device_id) d.onHdmiSelect(device_id);
        break;
      }
      case 'toggle_play':
        d.onTogglePlay();
        break;
      case 'next_media':
        if (d.getPodeAvancarMidia()) d.onNextMedia();
        break;
      case 'prev_media': {
        const len = d.getPlaylistLength();
        if (len > 0) d.onPrevMedia(len);
        break;
      }
      case 'toggle_mute':
        d.onToggleMute();
        break;
      case 'play_item': {
        const { index } = payload as { index: number };
        const len = d.getPlaylistLength();
        if (index >= 0 && index < len) d.onPlayItem(index);
        break;
      }
      case 'delete_fila_item': {
        const { id } = payload as { id: string };
        d.onDeleteFilaItem(id);
        playerRepository.atualizarStatusJukebox(id, 'concluido', d.telaId ?? '')
          .catch(err => console.warn('[Player] delete_fila_item:', err));
        break;
      }
      case 'toggle_qrcode':
        d.onToggleQrcode();
        break;
      case 'toggle_random':
        d.onToggleRandom();
        break;
      case 'force_reload':
        window.location.reload();
        break;
      case 'display_flash_message': {
        const { text, style, duration } = payload as {
          text: string; style: 'modal' | 'ticker'; duration: number;
        };
        d.onDisplayFlashMessage({ text, style, duration });
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => d.onClearFlashMessage(), duration * 1_000);
        break;
      }
      case 'set_ganho_captura': {
        const { ganho } = payload as { ganho: number };
        d.onSetGanhoCaptura(ganho);
        break;
      }
      case 'set_perfil_audio': {
        const { perfil } = payload as { perfil: PerfilAudio };
        d.onSetPerfilAudio(perfil);
        break;
      }
      case 'set_origem_midia': {
        const { origem, audio } = payload as { origem: FonteReproducao; audio: 'playlist' | 'captura' };
        d.onSetOrigemMidia(origem, audio);
        break;
      }
      case 'toggle_network_debug':
        d.onToggleNetworkDebug();
        break;
      case 'reload_playlist':
        void d.onReloadPlaylist();
        break;
      case 'hype_moment': {
        const { force } = payload as { force?: boolean };
        d.onHypeMoment(!!force);
        break;
      }
      case 'pdv_teste_destaque':
        d.onPdvTesteDestaque();
        break;
      case 'set_volume': {
        const { volume } = payload as { volume: number };
        if (typeof volume === 'number') d.onSetVolume(volume);
        break;
      }
    }
  };

  return { handleRemoteCmd };
}
